import { useState } from 'react'
import { Card, Row, Col, Badge, Button, Tabs, Tab, Table, Spinner } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { formatDateOnlyEsMX } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'

const TIPO_DOC_LABELS = {
  logo: 'Logo',
  identificacion: 'Identificación Oficial',
  comprobante_domicilio: 'Comprobante de Domicilio',
  acta_constitutiva: 'Acta Constitutiva',
  poder_notarial: 'Poder Notarial',
  cedula_fiscal: 'Cédula Fiscal',
  constancia_fiscal: 'Constancia de Situación Fiscal',
  contrato: 'Contrato de Servicios',
  otros: 'Otros'
}

const ClientDetail = ({ client, onEdit, onCreateContact, onCreateDocument, onEditContact, onEditDocument }) => {
  const [activeTab, setActiveTab] = useState('general')
  const [descargandoDocId, setDescargandoDocId] = useState(null)

  // Obtener contactos del cliente
  const { data: contacts } = useQuery(
    ['client-contacts', client.id],
    async () => {
      const response = await api.get(`/clients/contacts/?client_id=${client.id}`)
      return response.data.results || response.data || []
    },
    { enabled: !!client.id }
  )

  const handleDescargarDocumento = async (doc) => {
    if (!doc?.id) return
    setDescargandoDocId(doc.id)
    try {
      const response = await api.get(`/clients/documents/${doc.id}/download/`, {
        responseType: 'blob'
      })
      const ct = response.headers?.['content-disposition'] || ''
      let filename = (ct.match(/filename="?([^";\n]+)"?/) || [])[1]
      if (!filename) {
        const fileRef = doc.archivo ?? doc.file
        const fileStr = typeof fileRef === 'string' ? fileRef : ((fileRef?.name ?? fileRef?.url) ?? '')
        const ext = ((fileStr.split('/').pop()) || '').split('.').pop() || 'pdf'
        const nombre = (doc.descripcion ?? doc.description ?? doc.name ?? doc.nombre ?? `documento_${doc.id}`).replace(/[^a-zA-Z0-9._-]/g, '_')
        filename = `${nombre}.${ext}`
      }
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Documento descargado')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al descargar')
    } finally {
      setDescargandoDocId(null)
    }
  }

  // Obtener documentos del cliente
  const { data: documents } = useQuery(
    ['client-documents', client.id],
    async () => {
      const response = await api.get(`/clients/documents/?client_id=${client.id}`)
      return response.data.results || response.data || []
    },
    { enabled: !!client.id }
  )

  // Determinar tipo de persona legible
  const tipoCliente =
    client.type_name ||
    (client.type_tax_regime === 'fisica'
      ? 'Persona Física'
      : client.type_tax_regime === 'moral'
      ? 'Persona Moral'
      : client.tipo_cliente || 'N/A')

  // Determinar nombre/razón social a mostrar
  const esPersonaFisica =
    tipoCliente === 'Persona Física' ||
    client.type_tax_regime === 'fisica'

  const nombreCompletoFisica = [
    client.nombre || '',
    client.apellido_paterno || '',
    client.apellido_materno || ''
  ]
    .filter(Boolean)
    .join(' ')
    .trim()

  const displayName = esPersonaFisica
    ? (client.razon_social && client.razon_social.trim()) || nombreCompletoFisica || 'Sin nombre'
    : client.razon_social || client.business_name || 'Sin razón social'

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>{displayName}</h4>
        <div>
          <Badge
            bg={
              client.estado === 'activo'
                ? 'success'
                : client.estado === 'inactivo'
                ? 'secondary'
                : client.estado === 'suspendido'
                ? 'warning'
                : client.estado === 'baja'
                ? 'danger'
                : client.is_active
                ? 'success'
                : 'secondary'
            }
          >
            {client.estado === 'activo'
              ? 'Activo'
              : client.estado === 'inactivo'
              ? 'Inactivo'
              : client.estado === 'suspendido'
              ? 'Suspendido'
              : client.estado === 'baja'
              ? 'Baja'
              : client.is_active
              ? 'Activo'
              : 'Inactivo'}
          </Badge>
          <Button variant="outline-primary" size="sm" className="ms-2" onClick={onEdit}>
            Editar
          </Button>
        </div>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="general" title="Datos Generales">
          <Card>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>RFC:</strong> {client.rfc}</p>
                  <p><strong>Tipo:</strong> {tipoCliente}</p>
                  {esPersonaFisica ? (
                    <>
                      <p><strong>Nombre:</strong> {client.nombre || 'N/A'}</p>
                      <p><strong>Apellido Paterno:</strong> {client.apellido_paterno || 'N/A'}</p>
                      <p><strong>Apellido Materno:</strong> {client.apellido_materno || 'N/A'}</p>
                      {client.fecha_nacimiento && (
                        <p><strong>Fecha de Nacimiento:</strong> {formatDateOnlyEsMX(client.fecha_nacimiento, {}, '—')}</p>
                      )}
                      {client.curp && <p><strong>CURP:</strong> {client.curp}</p>}
                    </>
                  ) : (
                    <>
                      <p><strong>Razón Social:</strong> {client.razon_social || 'N/A'}</p>
                      {client.representante_legal && (
                        <p><strong>Representante Legal:</strong> {client.representante_legal}</p>
                      )}
                      {client.fecha_constitucion && (
                        <p><strong>Fecha de Constitución:</strong> {formatDateOnlyEsMX(client.fecha_constitucion, {}, '—')}</p>
                      )}
                    </>
                  )}
                </Col>
                <Col md={6}>
                  <p><strong>Email:</strong> {client.email || 'N/A'}</p>
                  <p><strong>Teléfono:</strong> {client.telefono || 'N/A'}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="fiscal" title="Datos Fiscales">
          <Card>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Dirección Fiscal:</strong></p>
                  <p>
                    {[client.calle, client.numero_exterior].filter(Boolean).join(' ')}
                    {client.numero_interior && ` Int. ${client.numero_interior}`}
                  </p>
                  <p>
                    {[client.colonia, client.municipio, client.estado_fiscal].filter(Boolean).join(', ')}
                  </p>
                  <p>
                    {[client.codigo_postal && `C.P. ${client.codigo_postal}`, client.pais].filter(Boolean).join(', ')}
                  </p>
                </Col>
                <Col md={6}>
                  {(client.regimen_fiscal_display || client.regimen_fiscal_codigo) && (
                    <p><strong>Régimen Fiscal:</strong> {client.regimen_fiscal_display || client.regimen_fiscal_codigo}</p>
                  )}
                  <p><strong>Nombre Comercial (PDF):</strong> {client.nombre_comercial_pdf || 'N/A'}</p>
                </Col>
              </Row>
              <Row className="mt-3 pt-3 border-top">
                <Col md={12}>
                  <p className="mb-2"><strong>Datos para transferencias SPEI</strong></p>
                </Col>
                <Col md={6}>
                  <p><strong>Banco:</strong> {client.banco_cobros || '—'}</p>
                  <p><strong>Cuenta para cobros:</strong> {client.cuenta_para_cobros || '—'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>CLABE:</strong> {client.clabe_cobros || 'No configurada'}</p>
                  <small className="text-muted">
                    Se muestra en el PDF de facturas para indicar a dónde depositar.
                  </small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="contable" title="Configuración Contable">
          <Card>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Clave contable (Cartera):</strong> {client.clave_display || '—'}</p>
                  <p><strong>Cuenta contable de activo:</strong> {client.cuenta_contable_activo_display || '—'}</p>
                  <p><strong>Tipo de Cierre:</strong> {client.tipo_cierre || 'N/A'}</p>
                  <p><strong>IVA Default:</strong> {client.iva_default ?? 0}%</p>
                  <p><strong>Moneda:</strong> {client.moneda || 'MXN'}</p>
                  <p><strong>Uso CFDI:</strong> {client.uso_cfdi || 'N/A'}</p>
                  {client.contador && (
                    <p><strong>Contador asignado:</strong> {client.contador_username || 'N/A'}</p>
                  )}
                </Col>
                <Col md={6}>
                  {client.cuenta_para_cobros && (
                    <p><strong>Cuenta para cobros:</strong> {client.cuenta_para_cobros}</p>
                  )}
                  {client.total_facturas !== undefined && (
                    <p><strong>Total Facturas:</strong> {client.total_facturas}</p>
                  )}
                  {client.total_pagos !== undefined && (
                    <p><strong>Total Pagos:</strong> {client.total_pagos}</p>
                  )}
                  {client.saldo_actual !== undefined && (
                    <p><strong>Saldo Actual:</strong> 
                      {formatCurrency(client.saldo_actual || 0)}
                    </p>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="contacts" title="Contactos">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Contactos</h5>
                {onCreateContact && (
                  <Button variant="primary" size="sm" onClick={onCreateContact}>
                    + Nuevo Contacto
                  </Button>
                )}
              </div>
              {contacts && contacts.length > 0 ? (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Tipo</th>
                      <th>Email</th>
                      <th>Teléfono</th>
                      <th>Notas</th>
                      <th>Principal</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr key={contact.id}>
                        <td>{contact.nombre ?? contact.name}</td>
                        <td>{contact.tipo_contacto || 'N/A'}</td>
                        <td>{contact.email || 'N/A'}</td>
                        <td>{contact.telefono_movil ?? contact.telefono ?? contact.phone ?? 'N/A'}</td>
                        <td>
                          <small className="text-muted">{String(contact.notas ?? contact.notes ?? '').trim() || '—'}</small>
                        </td>
                        <td>
                          <Badge bg={(contact.principal ?? contact.is_primary) ? 'success' : 'secondary'}>
                            {(contact.principal ?? contact.is_primary) ? 'Sí' : 'No'}
                          </Badge>
                        </td>
                        <td>
                          {onEditContact && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => onEditContact(contact)}
                            >
                              Editar
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted">No hay contactos registrados</p>
                  {onCreateContact && (
                    <Button variant="primary" size="sm" onClick={onCreateContact}>
                      Crear primer contacto
                    </Button>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="documents" title="Documentos">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Documentos</h5>
                {onCreateDocument && (
                  <Button variant="primary" size="sm" onClick={onCreateDocument}>
                    + Nuevo Documento
                  </Button>
                )}
              </div>
              {documents && documents.length > 0 ? (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Descripción</th>
                      <th>Fecha Vencimiento</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.id}>
                        <td>{TIPO_DOC_LABELS[doc.tipo_documento ?? doc.document_type ?? doc.tipo] ?? (doc.tipo_documento ?? doc.document_type ?? doc.tipo) ?? 'N/A'}</td>
                        <td>{doc.descripcion ?? doc.description ?? doc.name ?? doc.nombre ?? 'N/A'}</td>
                        <td>
                          {(() => {
                            const fechaVal = doc.expiry_date ?? doc.fecha_vencimiento
                            if (!fechaVal) return 'N/A'
                            return formatDateOnlyEsMX(fechaVal, {}, 'N/A')
                          })()}
                        </td>
                        <td>
                          <Badge bg={(doc.vigente ?? doc.valid) ? 'success' : 'secondary'}>
                            {(doc.vigente ?? doc.valid) ? 'Vigente' : 'Vencido'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleDescargarDocumento(doc)}
                              disabled={descargandoDocId === doc.id}
                              title="Descargar documento"
                            >
                              {descargandoDocId === doc.id ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-1" />
                                  Descargando...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-1"></i>
                                  Descargar
                                </>
                              )}
                            </Button>
                            {onEditDocument && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => onEditDocument(doc)}
                              >
                                Editar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted">No hay documentos registrados</p>
                  {onCreateDocument && (
                    <Button variant="primary" size="sm" onClick={onCreateDocument}>
                      Crear primer documento
                    </Button>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  )
}

export default ClientDetail

